/**
 * Copyright 2025 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */

import { type Locale } from '@/types/i18n';
import { type QuantitativeValue } from '@/types/seoSchema';
import dayjs, { type Dayjs } from '@/lib/dayjs';

const ISO_DATE_REGEX = /\dT\d/;

export function convertDate(
  today: Dayjs,
  dateInput: string,
  locale: Locale,
): QuantitativeValue | null {
  const baseLocale = locale.split('-')[0];

  const isoDate = dayjs(dateInput);
  if (ISO_DATE_REGEX.test(dateInput) && isoDate.isValid()) {
    const dayDifference = isoDate.diff(today, 'day');
    return {
      '@type': 'QuantitativeValue',
      minValue: dayDifference,
      maxValue: dayDifference,
      unitCode: 'd',
    };
  }

  if (dateInput.includes(': ')) {
    const parts = dateInput.split(': ');
    const dateString = parts[1].trim();

    if (dateInput.includes('-')) {
      const [firstDay, lastDay] = dateString.split('-').map((s) => s.trim());
      const startDate = parseAndAdjustDate(firstDay, today.year(), baseLocale, null);
      if (!startDate.isValid()) {
        return null;
      }
      let finalDate: Dayjs | null = null;
      const dayNumber = parseInt(lastDay);

      if (isNaN(dayNumber)) {
        finalDate = parseAndAdjustDate(lastDay, today.year(), baseLocale, startDate);
      } else {
        finalDate = startDate.clone().set('date', dayNumber);
      }
      if (!finalDate.isValid()) {
        return null;
      }
      return {
        '@type': 'QuantitativeValue',
        minValue: startDate.diff(today, 'day'),
        maxValue: finalDate.diff(today, 'day'),
        unitCode: 'd',
      };
    }

    const parsedDate = parseAndAdjustDate(dateString, today.year(), baseLocale, today);

    if (!parsedDate.isValid()) {
      return null;
    }

    const dayDifference = parsedDate.diff(today, 'day');
    return {
      '@type': 'QuantitativeValue',
      minValue: dayDifference,
      maxValue: dayDifference,
      unitCode: 'd',
    };
  }

  return null;
}

function parseAndAdjustDate(
  dateString: string,
  year: number,
  locale: string,
  dateBefore: Dayjs | null,
) {
  const cleanInput = dateString.replace('.', '');
  const parsedDate = dayjs(cleanInput, ['MMMM DD', 'MMM DD'], locale);

  const date = parsedDate.set('year', year).set('hour', 23);

  if (dateBefore !== null && date.isBefore(dateBefore)) {
    return date.add(1, 'year');
  }

  return date;
}
